import React, { useCallback, useEffect } from "react";
import { Box, IconButton, Typography } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ClearIcon from "@material-ui/icons/Clear";
import { useAlert, useProcessingStatus, usePreviousProp, useConfirmationDialog } from "hooks";
import { useHistory } from "react-router-dom";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { useTranslation } from "react-i18next";

interface RowControls {
    id?: string | number;
    redirectTo?: string;
    hasError: boolean;
    isProcessing: boolean;
    isCopyHidden?: boolean;
    disableControls?: boolean;
    onEdit(id: string | number): void;
    onDelete(id: string | number): void;
    onCopy?(id: string | number): void;
}

const RowControls: React.FC<RowControls> = ({
    id = "",
    hasError,
    isProcessing,
    isCopyHidden = false,
    disableControls = false,
    onEdit,
    onCopy,
    onDelete,
    redirectTo,
}) => {
    const { push } = useHistory();
    const { t } = useTranslation();
    const {
        confirmationId,
        isConfirmationOpen,
        handleOnOpen,
        handleOnClose,
        handleOnExited,
        handleOnConfirm,
    } = useConfirmationDialog((id) => onDelete(id));
    const { toggleAlert } = useAlert();
    const isProcessingPrev = usePreviousProp(isProcessing);
    useProcessingStatus(isProcessing, hasError);

    useEffect(() => {
        if (!isProcessing && isProcessingPrev && !hasError) {
            toggleAlert({ message: t("rows.label.rowDeleted", { id }), type: "success" });
            handleOnClose();
        }
    }, [id, toggleAlert, isProcessing, isProcessingPrev, hasError, handleOnClose, t]);

    const editRule = useCallback(
        (id: string | number) => () => {
            onEdit(id);

            if (redirectTo) {
                push(redirectTo);
            }
        },
        [push, redirectTo, onEdit]
    );

    const copyRule = useCallback(
        (id: string | number) => () => {
            if (onCopy) {
                onCopy(id);
            }

            if (redirectTo) {
                push(redirectTo);
            }
        },
        [push, onCopy, redirectTo]
    );

    return (
        <>
            <Box>
                <IconButton disabled={disableControls} onClick={editRule(id)}>
                    <EditIcon color={disableControls || isProcessing ? "disabled" : "primary"} fontSize="small" />
                </IconButton>
                {!isCopyHidden && (
                    <IconButton onClick={copyRule(id)} disabled={disableControls || isProcessing}>
                        <FileCopyIcon
                            color={disableControls || isProcessing ? "disabled" : "primary"}
                            fontSize="small"
                        />
                    </IconButton>
                )}
                <IconButton onClick={handleOnOpen(id)} disabled={disableControls || isProcessing}>
                    <ClearIcon color={disableControls || isProcessing ? "disabled" : "error"} fontSize="small" />
                </IconButton>
            </Box>
            <ConfirmationDialog isOpen={isConfirmationOpen} onClose={handleOnConfirm} onExited={handleOnExited}>
                <Typography variant={"body2"}>{t("row.label.deleteWarning", { confirmationId })}</Typography>
            </ConfirmationDialog>
        </>
    );
};

export const Controls = React.memo<RowControls>(
    RowControls,
    (prevProps, nextProps) =>
        prevProps.id === nextProps.id &&
        prevProps.isProcessing === nextProps.isProcessing &&
        prevProps.disableControls === nextProps.disableControls
);
